import React from 'react';
import { Button, Space, Tag, Typography } from 'antd';
import { ClockCircleOutlined, CloseOutlined, PhoneOutlined } from '@ant-design/icons';
import { callbackSourceTag } from '@/services/app/tenant/call-queue';
import { QueueItem } from '@/contexts/CallbackQueueContext';
import { dtFormat } from '@/util';

/**
 * "Oct 7, 03:04 PM" in the user's timezone. Backend datetimes are naive 'Y-m-d H:i:s'
 * strings in the SERVER timezone, so they go through dtFormat (parseDt) — never a raw
 * `new Date(str)`, which reads them as browser-local time and shifts them by hours.
 */
export const fmtCallbackDate = (serverDt: string) => dtFormat(serverDt, 'MMM D, hh:mm A', serverDt);

interface Props {
  item:       QueueItem;
  updatingId: number | null;
  onCallBack: (item: QueueItem) => void;
  onDismiss:  (item: QueueItem) => void;
}

/**
 * One pending callback, with its Call Back / Dismiss actions. Rendered by the nav badge
 * popover and by the expanded site-wide banner so the two never drift apart.
 * Layout is block-level (no List.Item wrapper) so the caller decides the container.
 */
const CallbackQueueItem: React.FC<Props> = ({ item, updatingId, onCallBack, onDismiss }) => {
  const source = callbackSourceTag(item.source);
  const busy   = updatingId === item.id;
  const locked = updatingId !== null && !busy;

  return (
    <div style={{ padding: '10px 4px', borderBottom: '1px solid #f0f0f0' }}>
      {/* name · phone */}
      <Typography.Text strong style={{ fontSize: 13 }}>
        {item.customer_name ? `${item.customer_name} · ` : ''}
        {item.customer_phone}
      </Typography.Text>
      {source && (
        <Tag color={source.color} style={{ fontSize: 10, marginLeft: 6, lineHeight: '16px' }}>
          {source.label}
        </Tag>
      )}

      {/* reason */}
      {item.reason && <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>{item.reason}</div>}

      {/* already-contacted nudge — an outbound call to this number already happened */}
      {item.contacted_at && (
        <div style={{ marginTop: 4 }}>
          <Tag color="green" icon={<PhoneOutlined />} style={{ fontSize: 11 }}>
            Already contacted · {fmtCallbackDate(item.contacted_at)}
          </Tag>
          {(item.contacted_by || item.contacted_from) && (
            <div style={{ fontSize: 11, color: '#389e0d', marginTop: 2 }}>
              {item.contacted_by ? `by ${item.contacted_by}` : ''}
              {item.contacted_by && item.contacted_from ? ' · ' : ''}
              {item.contacted_from ? `from ${item.contacted_from}` : ''}
            </div>
          )}
        </div>
      )}

      {/* meta */}
      <div style={{ fontSize: 11, color: '#999', margin: '4px 0' }}>
        <ClockCircleOutlined style={{ marginRight: 4 }} />
        {fmtCallbackDate(item.created_at)}
        <span style={{ margin: '0 6px' }}>·</span>
        Attempts: {item.attempts}
      </div>

      {/* notes */}
      {item.notes && (
        <div style={{ fontSize: 11, color: '#777', marginBottom: 6, fontStyle: 'italic' }}>{item.notes}</div>
      )}

      {/* actions */}
      <Space size={4} style={{ marginTop: 6 }}>
        <Button
          type="primary"
          size="small"
          icon={<PhoneOutlined />}
          loading={busy}
          disabled={locked}
          onClick={() => onCallBack(item)}
        >
          Call Back
        </Button>
        <Button size="small" icon={<CloseOutlined />} loading={busy} disabled={locked} onClick={() => onDismiss(item)}>
          Dismiss
        </Button>
      </Space>
    </div>
  );
};

export default CallbackQueueItem;
